import React, { useCallback, useEffect } from "react";
import { TicketMeeting } from "@services/tickets/types";
import {
  Autocomplete,
  Chip,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTranslation } from "react-i18next";
import { Add } from "@mui/icons-material";
import { useContext, useState } from "react";
import AddMeetingDialog from "../../AddMeetingDialog";
import PickMeetingDialog from "../../PickMeetingDialog";
import { MeetingInstance } from "@services/meetings/types";
import { FormContext } from "../../context/formContext";

type MeetingAffectedProps = {};

const MeetingAffected = (props: MeetingAffectedProps) => {
  const { t } = useTranslation();
  const { dispatchTicket, form, canEdit, dispatchHasChanged } =
    useContext(FormContext);
  const { ticket } = form;
  const [openAddMeetingDialog, setOpenAddMeetingDialog] =
    useState<boolean>(false);
  const [openPickMeetingDialog, setOpenPickMeetingDialog] =
    useState<boolean>(false);
  const [meetings, setMeetings] = useState<MeetingInstance[]>([]);
  const optionItems: readonly TicketMeeting[] = ticket.meetings ?? [];
  const disabled = ticket?.status == "Closed" || !canEdit;
  const [errorMeeting, setErrorMeeting] = useState<string>("");

  const displayContent = () => {
    return (
      <Autocomplete
        multiple
        size="small"
        freeSolo={true}
        value={ticket.meetings ?? []}
        id="meeting-tags-filled"
        options={optionItems}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        disabled={disabled}
        fullWidth
        onChange={(event, value) => {
          dispatchTicket({
            ...ticket,
            meetings: [...(value as TicketMeeting[])],
          });
          dispatchHasChanged(true);
        }}
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === "string") {
            return option;
          }

          // Regular option
          return "" + option.confirmationNumber;
        }}
        renderTags={(value, getTagProps) => {
          return value
            .sort(
              (a: TicketMeeting, b: TicketMeeting) =>
                (a.confirmationNumber ?? 0) - (b.confirmationNumber ?? 0)
            )
            .map((option: TicketMeeting, index) => (
              <Chip
                size="small"
                variant="outlined"
                label={option.confirmationNumber}
                {...getTagProps({ index })}
              />
            ));
        }}
        renderOption={(props, option) => (
          <li {...props}>{option.confirmationNumber}</li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Meeting")}
            slotProps={{
              htmlInput: { ...params.inputProps, disabled: true }
            }}
          />
        )}
      />
    );
  };

  const onMustPickMeeting = (meetings: MeetingInstance[]) => {
    setOpenAddMeetingDialog(false);
    setMeetings(meetings);
    setOpenPickMeetingDialog(true);
  };

  return (
    <>
      <Grid
        container
        sx={(theme) => ({
          padding: theme.spacing(2, 4),
          flexDirection: "column",
        })}
        spacing={2}
      >
        <Grid>
          <Grid
            container
            spacing={2}
            sx={{ width: '100%', flexDirection: "row", }}
          >
            <Grid>{t("Meeting Affected")}</Grid>

            <Tooltip title={t("Add") as string} arrow>
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => setOpenAddMeetingDialog(true)}
                  disabled={disabled}
                  sx={{
                    marginTop: 1.5,
                    marginLeft: 0.75,
                  }}
                >
                  <Add />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{ width: '100%', flexDirection: "row", }}
          >
            {displayContent()}
          </Grid>
          <FormControl
            sx={(theme) => ({
              width: "100%",
              "& .MuiFormHelperText-root": {
                color: theme.palette.error.main,
              },
              "& .MuiInputLabel-root::after": {
                content: "'*'",
                color: theme.palette.error.main,
              },
            })}
          >
            <FormHelperText>{errorMeeting}</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <AddMeetingDialog
        open={openAddMeetingDialog}
        onClose={() => setOpenAddMeetingDialog(false)}
        onMustPickMeeting={onMustPickMeeting}
      ></AddMeetingDialog>
      <PickMeetingDialog
        meetings={meetings}
        open={openPickMeetingDialog}
        onClose={() => setOpenPickMeetingDialog(false)}
      ></PickMeetingDialog>
    </>
  );
};

export default MeetingAffected;
